import { Dialog, Popover } from '@base-ui/react'
import { track } from '@cartwheel/analytics'
import { useCreateReferrerTourCompletionMutation } from '@cartwheel/api/v1'
import { Button } from '@cartwheel/components'
import clsx from 'clsx'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { type AnchorRect, findVisibleAnchor, useElementRect } from './use-element-rect'

// First-login product tour for school referrers, mounted on the external
// dashboard. Two phases: an intro carousel explaining what Cartwheel does, then
// spotlight coachmarks anchored to real sections of the page.
//
// Rendering is gated server-side — FirstLoginTourPropsBuilder returns nil unless
// the user is an eligible school referrer who has not seen the tour — so this
// component assumes it should run as soon as it mounts.
//
// Copy lives here as plain constants rather than in config/locales. A school
// referrer has no way to view this dashboard in another language (locale comes
// only from `?locale=`, there is no switcher, and users have no locale
// preference), so translating it would produce strings nobody can reach. That is
// a decision, not an oversight; revisit if staff locale preferences arrive.

type IconTone = 'mint' | 'sky' | 'gold' | 'lavender'

const ICON_TONES: Record<IconTone, string> = {
  mint: 'bg-mint-25 text-forest',
  sky: 'bg-sky-25 text-sea',
  gold: 'bg-gold-25 text-bronze',
  lavender: 'bg-lavender-25 text-indigo',
}

interface TourSlideBody {
  paragraphs: string[]
  /** Numbered list, used by the "how it works" slide. */
  steps?: string[]
  tip?: {
    tone: IconTone
    label: string
    text: string
    /** Rendered after `text`. Kept structured rather than inlining JSX so slide
        content stays declarative data. */
    link?: { href: string; label: string }
  }
}

interface IntroSlide {
  eyebrow: string
  icon: string
  iconTone: IconTone
  title: string
  body: TourSlideBody
  /** Label for the final slide's primary action. */
  ctaLabel?: string
}

/** Keys of FirstLoginTourPropsBuilder::ANCHORS, camelized for island props. */
type AnchorKey = 'actionItems' | 'navReferrals' | 'navStudents' | 'referCta' | 'navToggle'

interface Coachmark {
  anchorKey: AnchorKey
  /** Used instead of `anchorKey` below the `lg` breakpoint. */
  mobileAnchorKey?: AnchorKey
  icon: string
  title: string
  body: string
  /** Used instead of `body` when `mobileAnchorKey` is in play. */
  mobileBody?: string
  /** Used instead of `body` for users who can approve referrals. */
  approverBody?: string
}

const INTRO_SLIDES: IntroSlide[] = [
  {
    eyebrow: 'Welcome to Cartwheel',
    icon: 'fa-regular fa-hand-wave',
    iconTone: 'mint',
    title: 'Behavioral health care, built around your students',
    body: {
      paragraphs: [
        'Cartwheel partners with your school to connect students and families to therapy, often with little to no out-of-pocket cost. Let’s walk through how it works.',
      ],
    },
  },
  {
    eyebrow: 'Virtual care',
    icon: 'fa-regular fa-video',
    iconTone: 'sky',
    title: 'Meeting students where they are',
    body: {
      paragraphs: [
        'Cartwheel services are entirely virtual, giving your students access to more qualified clinicians. Sessions can be completed at home or at school.',
      ],
      tip: {
        tone: 'sky',
        label: 'Tip:',
        text: 'When submitting a referral, you can indicate if a student needs to complete sessions from school.',
      },
    },
  },
  {
    eyebrow: 'Who it’s for',
    icon: 'fa-regular fa-user-group',
    iconTone: 'lavender',
    title: 'A good fit for a wide range of students',
    body: {
      paragraphs: [
        'Think peer conflict, family stress, depressive symptoms, anxiety, or a history of self-harm or school avoidance. Direct therapy is for grades 3–12; parent guidance is available PreK–12.',
      ],
      tip: {
        tone: 'lavender',
        label: 'Tip:',
        text: 'A few things we can’t support virtually: primary eating disorders, primary substance use disorders, and significant violence or homicidal ideation. Ask your Success Manager if you’re ever unsure.',
      },
    },
  },
  {
    eyebrow: 'How it works',
    icon: 'fa-regular fa-route',
    iconTone: 'gold',
    title: 'Submitting a referral takes about 5 minutes',
    body: {
      paragraphs: [],
      steps: [
        'You submit a referral with basic information about the student',
        'We handle the rest: insurance verification, finding a clinician, and scheduling sessions',
        'You stay in the loop — we’ll share updates here on the student’s progress',
      ],
      tip: {
        tone: 'gold',
        label: 'Tip:',
        text: 'Want more detail about Cartwheel?',
        link: {
          href: 'https://learn.cartwheelcare.org/cartwheel-async-training',
          label: 'Take the at-home training here.',
        },
      },
    },
  },
  {
    eyebrow: 'Your home base',
    icon: 'fa-regular fa-compass',
    iconTone: 'gold',
    title: 'Let us show you around',
    body: {
      paragraphs: [
        'This is where you’ll start a referral, track the students you’ve referred, and see what needs your attention. We’ll point out the few things you’ll use most — it takes about a minute.',
      ],
    },
    ctaLabel: 'Get started',
  },
]

const COACHMARKS: Coachmark[] = [
  {
    anchorKey: 'actionItems',
    icon: 'fa-regular fa-list-check',
    title: 'Team Action Items',
    // Forward-looking on purpose: a brand-new referrer has no referrals yet, so
    // this section shows an empty state when the tour first runs.
    body: 'Your to-do list. When a referral needs something from your team — a missing email, a family we can’t reach — it shows up here with the next step.',
  },
  {
    anchorKey: 'navReferrals',
    icon: 'fa-regular fa-file-medical',
    title: 'Referrals',
    // Everyone gets this coachmark; only the sentence changes. District Admins
    // and School Admins land here to act on their staff's referrals, while
    // everyone else is tracking their own.
    body: 'View your submitted referrals.',
    approverBody: 'Review and approve referrals submitted by your school staff.',
  },
  {
    anchorKey: 'navStudents',
    icon: 'fa-regular fa-user-group',
    title: 'Students',
    body: 'See the students connected to your school, their care status, and who at Cartwheel to contact about each one.',
  },
  {
    anchorKey: 'referCta',
    mobileAnchorKey: 'navToggle',
    icon: 'fa-regular fa-circle-plus',
    title: 'Refer a student anytime',
    body: 'Whenever you’re worried about a student, start here. A referral takes about five minutes.',
    mobileBody:
      'Whenever you’re worried about a student, tap the menu and choose “Refer a Student.” It takes about five minutes.',
  },
]

const SPOTLIGHT_PADDING = 12
const MOBILE_QUERY = '(max-width: 1023px)'
const RESUME_KEY = 'cartwheel.referrer-tour.step'
/** Distinguishes the "Refer now" exit from "Finish tour" in the funnel. */
const REFER_NOW_STEP = 'refer_now'

/**
 * Analytics names for each coachmark. Mapped rather than reusing the anchor keys
 * directly so `last_step` stays snake_case throughout — every value in that field
 * should share one shape, or the drop-off funnel has to know several.
 */
const STEP_NAMES: Record<AnchorKey, string> = {
  actionItems: 'action_items',
  navReferrals: 'nav_referrals',
  navStudents: 'nav_students',
  referCta: 'refer_cta',
  navToggle: 'nav_toggle',
}
/** Above the Rails navbar and user dropdown, which sit at z-50. */
const SCRIM_Z = 70

interface FirstLoginTourProps {
  /** camelCase anchor key -> `data-tour-id` value, from the props builder. */
  anchors: Record<string, string>
  /** ReferralPolicy#approve_any? — selects each coachmark's `approverBody`. */
  isApprover?: boolean
  /** Where "Refer now" sends the user; resolved server-side. */
  referPath?: string
}

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia?.(MOBILE_QUERY).matches ?? false)

  useEffect(() => {
    const media = window.matchMedia?.(MOBILE_QUERY)
    if (!media) return
    const handler = (event: MediaQueryListEvent) => setIsMobile(event.matches)
    media.addEventListener('change', handler)
    return () => media.removeEventListener('change', handler)
  }, [])

  return isMobile
}

export default function FirstLoginTour({
  anchors,
  isApprover = false,
  referPath,
}: FirstLoginTourProps) {
  const isMobile = useIsMobile()
  const resumed = useRef(readResumeState())

  const [phase, setPhase] = useState<'intro' | 'coach' | 'done'>(resumed.current.phase)
  const [introIndex, setIntroIndex] = useState(resumed.current.introIndex)
  const [coachIndex, setCoachIndex] = useState(resumed.current.coachIndex)

  const { mutate: recordCompletion, mutateAsync: recordCompletionAsync } =
    useCreateReferrerTourCompletionMutation()
  const hasTrackedShown = useRef(false)

  // Resolve the coachmark list once, when the coachmark phase begins, keeping
  // only those whose anchor is actually on the page. Doing it up front rather
  // than lazily means the step counter ("2 of 4") can never disagree with what
  // the user will actually be shown. The action-items section is the reason this
  // is needed: it sits behind another team's feature flag.
  const [resolved, setResolved] = useState<Coachmark[] | null>(null)

  const anchorFor = useCallback(
    (coachmark: Coachmark) => {
      const key =
        isMobile && coachmark.mobileAnchorKey ? coachmark.mobileAnchorKey : coachmark.anchorKey
      return anchors[key] ?? null
    },
    [anchors, isMobile]
  )

  // Mobile wins over the approver variant: when a coachmark has swapped to a
  // different anchor for the breakpoint, its copy has to describe that anchor.
  const bodyFor = useCallback(
    (coachmark: Coachmark) => {
      if (isMobile && coachmark.mobileAnchorKey && coachmark.mobileBody) {
        return coachmark.mobileBody
      }
      if (isApprover && coachmark.approverBody) return coachmark.approverBody
      return coachmark.body
    },
    [isMobile, isApprover]
  )

  useEffect(() => {
    if (hasTrackedShown.current) return
    hasTrackedShown.current = true
    track('REFERRER_ONBOARDING_TOUR_SHOWN', {})
  }, [])

  // Persist progress so a Turbo navigation mid-tour resumes instead of
  // restarting: the island is torn down on `turbo:before-cache` and remounted.
  useEffect(() => {
    if (phase === 'done') {
      window.sessionStorage.removeItem(RESUME_KEY)
      return
    }
    window.sessionStorage.setItem(RESUME_KEY, JSON.stringify({ phase, introIndex, coachIndex }))
  }, [phase, introIndex, coachIndex])

  const finish = useCallback(
    (outcome: 'completed' | 'skipped', lastStep: string) => {
      setPhase('done')
      window.sessionStorage.removeItem(RESUME_KEY)
      track('REFERRER_ONBOARDING_TOUR_DISMISSED', { outcome, lastStep })
      // Dismiss locally regardless of the result. If this write fails the tour
      // reappears next login, which is far better than trapping the user under
      // an overlay they cannot get rid of.
      recordCompletion({ body: { outcome, last_step: lastStep } })
    },
    [recordCompletion]
  )

  // "Refer now" is the one exit that navigates, which makes the write ordering
  // matter: fire-and-forget would race page unload and often be cancelled,
  // re-showing the tour on the user's next login. So this awaits the write, then
  // leaves — and still leaves if the write fails, since a repeated tour beats
  // being stranded. The overlay is dismissed first, so there is no window in
  // which the button can be clicked twice.
  const finishAndRefer = useCallback(async () => {
    if (!referPath) return

    setPhase('done')
    window.sessionStorage.removeItem(RESUME_KEY)
    track('REFERRER_ONBOARDING_TOUR_DISMISSED', {
      outcome: 'completed',
      lastStep: REFER_NOW_STEP,
    })

    try {
      await recordCompletionAsync({
        body: { outcome: 'completed', last_step: REFER_NOW_STEP },
      })
    } catch {
      // Intentionally swallowed — see above.
    }

    window.location.assign(referPath)
  }, [recordCompletionAsync, referPath])

  // Single definition of "which coachmarks are live", used by both entry points
  // below. A fresh run and a resumed run must agree on the set, or the step
  // counter and the steps shown diverge.
  const presentCoachmarks = useCallback(
    () =>
      COACHMARKS.filter((coachmark) => {
        const id = anchorFor(coachmark)
        return id ? findVisibleAnchor(id) !== null : false
      }),
    [anchorFor]
  )

  const startCoachmarks = useCallback(() => {
    const present = presentCoachmarks()

    if (present.length === 0) {
      finish('completed', 'intro')
      return
    }

    setResolved(present)
    setCoachIndex(0)
    setPhase('coach')
  }, [presentCoachmarks, finish])

  // A resumed session lands directly in the coachmark phase without passing
  // through startCoachmarks, so resolve the list here too.
  useEffect(() => {
    if (phase !== 'coach' || resolved !== null) return

    const present = presentCoachmarks()
    if (present.length === 0) {
      finish('completed', 'resume')
      return
    }

    setResolved(present)
    setCoachIndex((index) => Math.min(index, present.length - 1))
  }, [phase, resolved, presentCoachmarks, finish])

  if (phase === 'done') return null

  if (phase === 'intro') {
    const slide = INTRO_SLIDES[introIndex]
    if (!slide) return null

    return (
      <TourIntro
        slide={slide}
        index={introIndex}
        total={INTRO_SLIDES.length}
        onBack={() => setIntroIndex((index) => Math.max(0, index - 1))}
        onNext={() => {
          if (introIndex >= INTRO_SLIDES.length - 1) {
            startCoachmarks()
          } else {
            setIntroIndex((index) => index + 1)
          }
        }}
        onSkip={() => finish('skipped', `intro_${introIndex + 1}`)}
      />
    )
  }

  const coachmarks = resolved
  if (!coachmarks || coachmarks.length === 0) return null

  const current = coachmarks[Math.min(coachIndex, coachmarks.length - 1)]
  if (!current) return null

  const isLast = coachIndex >= coachmarks.length - 1
  const advance = () => {
    if (isLast) {
      finish('completed', STEP_NAMES[current.anchorKey])
    } else {
      setCoachIndex((index) => index + 1)
    }
  }

  return (
    <CoachmarkStep
      // Remount per step so Base UI re-runs its open/focus transition and the
      // anchor hook re-resolves from scratch.
      key={current.anchorKey}
      tourId={anchorFor(current)}
      icon={current.icon}
      title={current.title}
      body={bodyFor(current)}
      index={coachIndex}
      total={coachmarks.length}
      onAdvance={advance}
      onBack={() => setCoachIndex((index) => Math.max(0, index - 1))}
      onSkip={() => finish('skipped', STEP_NAMES[current.anchorKey])}
      onFinish={() => finish('completed', STEP_NAMES[current.anchorKey])}
      onRefer={referPath ? finishAndRefer : undefined}
      onMissingAnchor={advance}
    />
  )
}

function readResumeState(): {
  phase: 'intro' | 'coach'
  introIndex: number
  coachIndex: number
} {
  const fallback = { phase: 'intro' as const, introIndex: 0, coachIndex: 0 }
  try {
    const raw = window.sessionStorage.getItem(RESUME_KEY)
    if (!raw) return fallback
    const parsed = JSON.parse(raw) as Partial<{
      phase: string
      introIndex: number
      coachIndex: number
    }>
    if (parsed.phase !== 'intro' && parsed.phase !== 'coach') return fallback
    return {
      phase: parsed.phase,
      introIndex: Number.isInteger(parsed.introIndex) ? (parsed.introIndex as number) : 0,
      coachIndex: Number.isInteger(parsed.coachIndex) ? (parsed.coachIndex as number) : 0,
    }
  } catch {
    return fallback
  }
}

function ProgressDots({ ids, activeIndex }: { ids: string[]; activeIndex: number }) {
  return (
    <div className="flex items-center gap-2" aria-hidden="true">
      {ids.map((id, index) => (
        <span
          key={id}
          className={clsx(
            'h-1.5 rounded-full transition-all duration-300',
            index === activeIndex ? 'w-6 bg-forest' : 'w-1.5 bg-sand-dark'
          )}
        />
      ))}
    </div>
  )
}

function SlideBody({ body }: { body: TourSlideBody }) {
  return (
    <div className="flex flex-col gap-4">
      {body.paragraphs.map((paragraph) => (
        <p key={paragraph} className="leading-relaxed text-charcoal-75">
          {paragraph}
        </p>
      ))}

      {body.steps && (
        <ol className="flex flex-col gap-3">
          {body.steps.map((step, index) => (
            <li key={step} className="flex items-start gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-mint-25 text-sm font-semibold text-forest">
                {index + 1}
              </span>
              <span className="flex-1 text-charcoal-75">{step}</span>
            </li>
          ))}
        </ol>
      )}

      {body.tip && (
        <div className={clsx('flex items-start gap-3 rounded-lg p-3', ICON_TONES[body.tip.tone])}>
          <i className="fa-regular fa-lightbulb mt-0.5" aria-hidden="true" />
          <p className="flex-1 text-sm text-charcoal-75">
            <span className="font-semibold text-charcoal">{body.tip.label}</span> {body.tip.text}
            {body.tip.link && (
              <>
                {' '}
                <a
                  href={body.tip.link.href}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-indigo underline"
                >
                  {body.tip.link.label}
                </a>
              </>
            )}
          </p>
        </div>
      )}
    </div>
  )
}

function TourIntro({
  slide,
  index,
  total,
  onBack,
  onNext,
  onSkip,
}: {
  slide: IntroSlide
  index: number
  total: number
  onBack: () => void
  onNext: () => void
  onSkip: () => void
}) {
  const isLast = index === total - 1
  const popupRef = useRef<HTMLDivElement>(null)

  return (
    <Dialog.Root
      open
      // trap-focus rather than true: a modal dialog locks body scroll, and the
      // coachmark phase that follows needs to scroll the page.
      modal="trap-focus"
      onOpenChange={(open, details) => {
        if (!open && details.reason === 'escape-key') onSkip()
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 bg-charcoal/60" style={{ zIndex: SCRIM_Z }} />
        {/* Focus the dialog itself rather than its first focusable child, which
            is what Base UI would do by default. Button has no focus styles of its
            own, so the browser's default ring landed on it as a stray blue
            border. `outline-none` then suppresses that ring on the container:
            standard practice for a dialog panel, which receives focus only so
            screen readers announce its title and body — it is not a control the
            user tabs to, and the buttons inside still show focus when tabbed. */}
        <Dialog.Popup
          ref={popupRef}
          initialFocus={popupRef}
          className="fixed left-1/2 top-1/2 w-[calc(100vw-3rem)] max-w-[460px] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl bg-white shadow-xl focus:outline-none"
          style={{ zIndex: SCRIM_Z + 1 }}
        >
          <div className="flex flex-col gap-5 p-8">
            <span
              className={clsx(
                'flex h-14 w-14 items-center justify-center rounded-full text-2xl',
                ICON_TONES[slide.iconTone]
              )}
            >
              <i className={slide.icon} aria-hidden="true" />
            </span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-charcoal-50">
                {slide.eyebrow}
              </p>
              <Dialog.Title className="mt-1.5 font-ginto-nord-md text-2xl leading-tight text-charcoal">
                {slide.title}
              </Dialog.Title>
            </div>
            <Dialog.Description render={<div />}>
              <SlideBody body={slide.body} />
            </Dialog.Description>
          </div>

          <div className="flex items-center justify-between border-t border-gray-stroke px-8 py-4">
            <ProgressDots ids={INTRO_SLIDES.map((entry) => entry.eyebrow)} activeIndex={index} />
            <div className="flex items-center gap-2">
              {index > 0 ? (
                <Button variant="text" onClick={onBack}>
                  Back
                </Button>
              ) : (
                <Button variant="text" onClick={onSkip} data-testid="tour-intro-skip">
                  Skip tour
                </Button>
              )}
              <Button variant="primary" onClick={onNext} data-testid="tour-intro-next">
                {isLast ? (slide.ctaLabel ?? 'Show me around') : 'Next'}
              </Button>
            </div>
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function CoachmarkStep({
  tourId,
  icon,
  title,
  body,
  index,
  total,
  onAdvance,
  onBack,
  onSkip,
  onFinish,
  onRefer,
  onMissingAnchor,
}: {
  tourId: string | null
  icon: string
  title: string
  body: string
  index: number
  total: number
  onAdvance: () => void
  onBack: () => void
  onSkip: () => void
  onFinish: () => void
  /** Absent when no referral path was supplied; the last step then just closes. */
  onRefer?: () => void
  onMissingAnchor: () => void
}) {
  const { rect, element } = useElementRect(tourId, { onMissing: onMissingAnchor })
  const isLast = index === total - 1
  const popupRef = useRef<HTMLDivElement>(null)

  const anchor = useMemo(() => element ?? undefined, [element])

  return (
    <Popover.Root
      open
      modal="trap-focus"
      onOpenChange={(open, details) => {
        if (open) return
        // Escape on the final step means the user saw everything, so it is a
        // completion — recording it as a skip would misreport the funnel.
        if (details.reason === 'escape-key') {
          isLast ? onFinish() : onSkip()
        }
      }}
    >
      {/* Full-viewport click catcher. The spotlight's dimming comes from a huge
          box-shadow, and shadows do not receive pointer events, so the dimmed
          area needs its own layer. Clicking anywhere outside the card advances
          rather than dismissing — the conventional coachmark affordance, and it
          cannot accidentally record a skip. */}
      <div
        className="fixed inset-0"
        style={{ zIndex: SCRIM_Z }}
        onClick={onAdvance}
        aria-hidden="true"
      />

      <Spotlight rect={rect} />

      <Popover.Portal>
        <Popover.Positioner
          anchor={anchor}
          side="bottom"
          align="center"
          sideOffset={SPOTLIGHT_PADDING + 8}
          collisionPadding={16}
          style={{ zIndex: SCRIM_Z + 1 }}
        >
          {/* See the note on Dialog.Popup: focus the card, not its first button. */}
          <Popover.Popup
            ref={popupRef}
            initialFocus={popupRef}
            className="w-[340px] max-w-[calc(100vw-2rem)] rounded-xl bg-white p-5 shadow-xl focus:outline-none"
          >
            <div className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-mint-25 text-forest">
                <i className={icon} aria-hidden="true" />
              </span>
              <div className="flex-1">
                <Popover.Title className="font-ginto-nord-md text-base text-charcoal">
                  {title}
                </Popover.Title>
                <Popover.Description className="mt-1 text-sm leading-relaxed text-charcoal-75">
                  {body}
                </Popover.Description>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <span className="text-xs font-medium text-charcoal-50">
                {index + 1} of {total}
              </span>
              {/* The last step closes on the action it just described: the
                  secondary becomes "Finish tour" in place of Back, and the
                  primary sends the user straight into a referral. Without a
                  referral path it falls back to plain "Got it". */}
              <div className="flex items-center gap-1.5">
                {isLast && onRefer ? (
                  <Button variant="text" onClick={onFinish} data-testid="tour-coach-finish">
                    Finish tour
                  </Button>
                ) : (
                  index > 0 && (
                    <Button variant="text" onClick={onBack}>
                      Back
                    </Button>
                  )
                )}
                {!isLast && (
                  <Button variant="text" onClick={onSkip} data-testid="tour-coach-skip">
                    Skip
                  </Button>
                )}
                <Button
                  variant="primary"
                  onClick={isLast ? (onRefer ?? onFinish) : onAdvance}
                  data-testid="tour-coach-next"
                >
                  {isLast ? (onRefer ? 'Refer now' : 'Got it') : 'Next'}
                </Button>
              </div>
            </div>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  )
}

/**
 * The lit cutout. A transparent box over the target with an oversized
 * box-shadow, which dims everything else while leaving the target visible.
 * Never interactive — the click catcher above owns pointer events.
 */
function Spotlight({ rect }: { rect: AnchorRect | null }) {
  if (!rect) return null

  return (
    <div
      // Hook for the Playwright geometry assertion: this is the one piece of
      // positioning no unit test can verify, because happy-dom has no layout.
      data-tour-cutout=""
      className="pointer-events-none fixed rounded-xl ring-2 ring-forest transition-all duration-300"
      style={{
        top: rect.top - SPOTLIGHT_PADDING,
        left: rect.left - SPOTLIGHT_PADDING,
        width: rect.width + SPOTLIGHT_PADDING * 2,
        height: rect.height + SPOTLIGHT_PADDING * 2,
        boxShadow: '0 0 0 9999px rgba(15, 27, 31, 0.62)',
        zIndex: SCRIM_Z,
      }}
      aria-hidden="true"
    />
  )
}
